import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';

import Popup from '../../../../components/Popup';
import Select from '../../../../components/Select';
import { I18n } from 'react-redux-i18n';
import { components } from 'apiTyped/types';
import { useArticleHeader } from 'apiTyped/hooks/articles';

import {
  usePostAddTagMutation,
  usePostRemoveTagMutation
} from 'apiTyped/hooks/post.mutations';

const Container = styled.div`
  min-height: 270px;
`;

type TagsModalProps = {
  articleIds: number[];
  open: boolean;
  onClose: () => void;
  onSubmit: (
    // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
    selectedArticles: number[],
    // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
    tagsToAdd: ({ id: number; name: string } | undefined)[],
    // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
    tagsToRemove: ({ id: number; name: string } | undefined)[]
  ) => void;
  articles: components['schemas']['Post'][];
};

type Option = {
  label: string;
  value: number | string;
};

export const TagsModal = ({
  articleIds,
  open,
  onClose,
  articles,
  onSubmit
}: TagsModalProps) => {
  const [selectedTags, setSelectedTags] = useState<Option[]>([]);

  const selectedArticles = useMemo(() => {
    return articles.filter((article) => articleIds.includes(article.id));
  }, [articles, articleIds]);

  const [inputValue, setInputValue] = useState('');

  const tagRemoveMutations = articles
    .map((article) => {
      return {
        mutation: usePostRemoveTagMutation(article.id),
        articleId: article.id
      };
    })
    .filter((mut) => articleIds.includes(mut.articleId));

  const tagAddMutations = articles
    .map((article) => {
      return {
        mutation: usePostAddTagMutation(article.id),
        articleId: article.id
      };
    })
    .filter((mut) => articleIds.includes(mut.articleId));

  const commonTagsFromSelectedArticles = useMemo(() => {
    const result = [] as Option[];

    selectedArticles.forEach((article) => {
      article.tags.forEach((tag) => {
        const formattedTag = {
          label: tag.name,
          value: tag.id
        };

        if (
          !result.find((tag) => tag.label === formattedTag.label) &&
          selectedArticles.every((article) =>
            article.tags.find((tag) => tag.name === formattedTag.label)
          )
        )
          result.push(formattedTag);
      });
    });
    return result;
  }, [selectedArticles]);

  useEffect(() => {
    setSelectedTags(commonTagsFromSelectedArticles);
  }, [commonTagsFromSelectedArticles]);

  const { articleHeader } = useArticleHeader();

  const availableTags =
    articleHeader?.tags.map((tag) => ({
      value: tag.id,
      label: tag.name
    })) || [];

  const tagsOtpions = [
    { label: I18n.t('media.optionTags.isFavorite'), value: 'is_favorite' },
    { label: I18n.t('media.optionTags.isFeatured'), value: 'is_featured' },
    { label: I18n.t('media.optionTags.isPaper'), value: 'is_paper' },
    ...availableTags
  ];

  const handleClose = () => {
    setSelectedTags([]);
    onClose();
  };

  const handleOnChange = (value: Option[]) => {
    setSelectedTags(value);
  };

  const getAddedTags = (tags: Option[], selectedTags: Option[]) => {
    return selectedTags.reduce((acc, curr) => {
      if (!tags.find((el) => el.label === curr.label)) {
        acc.push({ label: curr.label, value: curr.value });
      }
      return acc;
    }, [] as Option[]);
  };

  const getRemovedTags = (tags: Option[], selectedTags: Option[]) => {
    return tags.reduce((acc, curr) => {
      if (!selectedTags.find((el) => el.label === curr.label)) {
        acc.push(curr);
      }
      return acc;
    }, [] as Option[]);
  };

  const handleSubmit = async () => {
    const addedTags = getAddedTags(
      commonTagsFromSelectedArticles,
      selectedTags
    );
    const removedTags = getRemovedTags(
      commonTagsFromSelectedArticles,
      selectedTags
    );

    const removeMutation = tagRemoveMutations
      .map((item) => {
        return removedTags.map((tag) => {
          return item.mutation.mutateAsync({ name: tag.label });
        });
      })
      .flat();

    const addMutation = tagAddMutations
      .map((item) => {
        return addedTags.map((tag) => {
          return item.mutation.mutateAsync({ name: tag.label });
        });
      })
      .flat();

    const createdTags = await Promise.all(addMutation);
    const tagToAdd = createdTags.filter(
      (tag, index, self) => index === self.findIndex((t) => t?.id === tag?.id)
    );

    const tagRemoved = await Promise.all(removeMutation);

    onSubmit(articleIds, tagToAdd, tagRemoved);
    handleClose();
  };

  const onInputBlur = () => {
    if (!isValidNewOption(inputValue, selectedTags)) return;
    setSelectedTags((v) => {
      return [...(v ? v : []), { label: inputValue, value: inputValue }];
    });
  };

  const isValidNewOption = (inputValue: string, selectOptions: Option[]) => {
    if (
      inputValue.trim().length === 0 ||
      selectOptions.find((option) => option.label === inputValue)
    ) {
      return false;
    }
    return true;
  };

  const handleOnCreateOption = (value: string) => {
    setSelectedTags([...selectedTags, { label: value, value }]);
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  return (
    <Popup
      open={open}
      title={I18n.t('media.tagsModal.title')}
      onClose={handleClose}
      onOk={() => handleSubmit()}
    >
      <Container>
        <Select
          isMulti
          creatable
          isValidNewOption={(
            inputValue: string,
            _selectedValues: unknown,
            selectOtpions: Option[]
          ) => isValidNewOption(inputValue, selectOtpions)}
          onCreateOption={(val: any) => handleOnCreateOption(val)}
          label={I18n.t('media.tagsModal.selectLabel')}
          placeholder=""
          inputValue={inputValue}
          value={selectedTags}
          options={tagsOtpions}
          onChange={handleOnChange}
          dropdownProps={{ styles: 'margin-right: 10px' }}
          onBlur={onInputBlur}
          onInputChange={handleInputChange}
        />
      </Container>
    </Popup>
  );
};
